/**
 * Request tracker for observability.
 *
 * Tracks all fetch requests with:
 * - x-request-id (UUID)
 * - route, endpoint, status, durationMs, errorName
 * - Logs to console in dev, Sentry in prod
 *
 * Also provides a dev health panel (console command) to inspect system state.
 */

let requestCounter = 0;

export interface RequestLogEntry {
  requestId: string;
  route: string;
  endpoint: string;
  method: string;
  status: number | null;
  durationMs: number;
  errorName: string | null;
  errorMessage: string | null;
  timestamp: number;
}

const MAX_LOG_ENTRIES = 50;
const recentRequests: RequestLogEntry[] = [];

/**
 * Generate a unique request ID.
 */
export function generateRequestId(): string {
  requestCounter += 1;
  const ts = Date.now().toString(36);
  const rand = Math.random().toString(36).substring(2, 8);
  return `req_${ts}_${rand}_${requestCounter}`;
}

/**
 * Log a completed request.
 */
export function logRequest(entry: RequestLogEntry): void {
  recentRequests.push(entry);
  if (recentRequests.length > MAX_LOG_ENTRIES) {
    recentRequests.shift();
  }

  if (import.meta.env.DEV) {
    const statusStr = entry.status ? String(entry.status) : "ERR";
    const color = entry.status && entry.status < 400 ? "\x1b[32m" : "\x1b[31m";
    const reset = "\x1b[0m";
    console.log(
      `${color}[${statusStr}]${reset} ${entry.endpoint} (${entry.durationMs}ms) [${entry.requestId}]`,
      entry.errorName ? `ERROR: ${entry.errorName}: ${entry.errorMessage}` : ""
    );
  }
}

/**
 * Get recent request log entries.
 */
export function getRecentRequests(): ReadonlyArray<RequestLogEntry> {
  return recentRequests;
}

/**
 * Get the last failed request (for health panel).
 */
export function getLastFailedRequest(): RequestLogEntry | null {
  for (let i = recentRequests.length - 1; i >= 0; i--) {
    const entry = recentRequests[i];
    if (entry.status === null || entry.status >= 400) {
      return entry;
    }
  }
  return null;
}

/**
 * Dev/stage health panel: print system state to console.
 *
 * Call from browser console: window.__FINDIGO_HEALTH__()
 */
export function printHealthPanel(): void {
  // Inline import to avoid circular dependency; dynamic import not available in sync context.
  const maskedUrl = (() => {
    try {
      const stored = typeof window !== "undefined"
        ? ((window as unknown as { __RUNTIME_CONFIG__?: { SUPABASE_URL?: string } }).__RUNTIME_CONFIG__?.SUPABASE_URL ?? "")
        : "";
      const url = stored || "(env)";
      return url.replace(/https?:\/\/([a-z]{4})[a-z]+/, "https://$1***");
    } catch {
      return "(not set)";
    }
  })();

  const lastFailed = getLastFailedRequest();

  console.group("=== Findigo Health Panel ===");
  console.log("SUPABASE_URL:", maskedUrl);
  console.log("Auth session:", typeof window !== "undefined" ? "(check via supabase.auth.getSession())" : "N/A");
  console.log("Recent requests:", recentRequests.length);
  if (lastFailed) {
    console.warn("Last failed request:", {
      url: lastFailed.endpoint,
      status: lastFailed.status,
      requestId: lastFailed.requestId,
      errorName: lastFailed.errorName,
      durationMs: lastFailed.durationMs,
      when: new Date(lastFailed.timestamp).toISOString(),
    });
  } else {
    console.log("Last failed request: (none)");
  }
  console.log("Kill switch:", (() => {
    try { return localStorage.getItem("findigo_kill_switch") === "true"; } catch { return false; }
  })());
  console.groupEnd();
}

// Register global health panel command in dev
if (typeof window !== "undefined") {
  (window as unknown as { __FINDIGO_HEALTH__: typeof printHealthPanel }).__FINDIGO_HEALTH__ = printHealthPanel;
}
